import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import GroupBadge from 'flarum/common/components/GroupBadge';
import avatar from 'flarum/common/helpers/avatar';
import username from 'flarum/common/helpers/username';
import extractText from 'flarum/common/utils/extractText';
import Stream from 'flarum/common/utils/Stream';
import User from 'flarum/common/models/User';
import Group from 'flarum/common/models/Group';

const FILTER_THRESHOLD = 5;
const LOCALE = 'tr';

/**
 * The recipients currently selected in fof/byobu's AddRecipientModal, one row
 * each, with a visible remove button.
 *
 * Byobu's own list removes a recipient when its name is clicked, which only a
 * hover tooltip explains and which phones never show. This list edits the very
 * same `selected` ItemList, so saving still goes through byobu's onsubmit and
 * its server-side permission checks.
 *
 * With many recipients the hard part is finding the one to remove, so the
 * list is sorted (groups first, then users alphabetically) and gets a filter
 * box once it is long enough to need one.
 *
 * Attrs:
 *  - selected: Stream<ItemList<User|Group>>, owned by the modal
 *  - discussion: Discussion|undefined, set when editing an existing discussion
 */
export default class RecipientList extends Component {
  oninit(vnode) {
    super.oninit(vnode);

    this.filter = Stream('');
  }

  view() {
    const entries = Object.entries(this.attrs.selected().toObject())
      .filter(([, item]) => this.isRecipient(item.content))
      .sort(([, a], [, b]) => this.compare(a.content, b.content));

    const query = this.filter().trim().toLocaleLowerCase(LOCALE);
    const visible = query ? entries.filter(([, item]) => this.matches(item.content, query)) : entries;

    return (
      <div className="Form-group ByobuRecipients">
        <label>{app.translator.trans('happydigital-byobu-recipients.forum.recipients.heading', { count: entries.length })}</label>

        {entries.length > FILTER_THRESHOLD ? this.filterInput() : null}

        {visible.length ? <ul className="ByobuRecipients-list">{visible.map(([key, item]) => this.row(key, item.content))}</ul> : null}

        {!entries.length ? <p className="helpText">{app.translator.trans('happydigital-byobu-recipients.forum.recipients.empty')}</p> : null}

        {entries.length && !visible.length ? (
          <p className="helpText">{app.translator.trans('happydigital-byobu-recipients.forum.recipients.no_match')}</p>
        ) : null}
      </div>
    );
  }

  filterInput() {
    return (
      <input
        className="FormControl ByobuRecipients-filter"
        type="search"
        placeholder={extractText(app.translator.trans('happydigital-byobu-recipients.forum.recipients.filter_placeholder'))}
        value={this.filter()}
        oninput={(e) => this.filter(e.target.value)}
        onkeydown={(e) => {
          // The modal is a <form>; Enter here must not submit the recipients.
          if (e.key === 'Enter') e.preventDefault();
        }}
      />
    );
  }

  /**
   * Groups first, then the current user, then everyone else by name.
   */
  compare(a, b) {
    const rank = (recipient) => (recipient instanceof Group ? 0 : this.isCurrentUser(recipient) ? 1 : 2);

    if (rank(a) !== rank(b)) return rank(a) - rank(b);

    return this.nameOf(a).localeCompare(this.nameOf(b), LOCALE, { sensitivity: 'base' });
  }

  matches(recipient, query) {
    const haystack = [this.nameOf(recipient)];

    if (recipient instanceof User) haystack.push(recipient.username() || '');

    return haystack.some((text) => text.toLocaleLowerCase(LOCALE).indexOf(query) !== -1);
  }

  nameOf(recipient) {
    return (recipient instanceof User ? recipient.displayName() : recipient.namePlural()) || '';
  }

  /**
   * @param {string} key ItemList key, e.g. "users:12" or "groups:4"
   * @param {User|Group} recipient
   */
  row(key, recipient) {
    const name = this.nameOf(recipient);

    return (
      <li className="ByobuRecipients-item" key={key}>
        <span className="ByobuRecipients-avatar">
          {recipient instanceof User ? avatar(recipient) : GroupBadge.component({ group: recipient, label: null })}
        </span>

        <span className="ByobuRecipients-name">
          {recipient instanceof User ? username(recipient) : name}
          {this.isCurrentUser(recipient) ? (
            <span className="ByobuRecipients-you">{app.translator.trans('happydigital-byobu-recipients.forum.recipients.you')}</span>
          ) : null}
        </span>

        {this.canRemove(recipient)
          ? Button.component({
              type: 'button',
              className: 'Button Button--icon Button--link ByobuRecipients-remove',
              icon: 'fas fa-times',
              'aria-label': extractText(app.translator.trans('happydigital-byobu-recipients.forum.recipients.remove_label', { name })),
              title: extractText(app.translator.trans('happydigital-byobu-recipients.forum.recipients.remove')),
              onclick: () => this.attrs.selected().remove(key),
            })
          : null}
      </li>
    );
  }

  isRecipient(recipient) {
    return recipient instanceof User || recipient instanceof Group;
  }

  isCurrentUser(recipient) {
    return recipient instanceof User && !!app.session.user && recipient.id() === app.session.user.id();
  }

  /**
   * Mirrors the checks byobu uses to decide which search sources to offer:
   * on an existing discussion each recipient type has its own permission, in
   * the composer the user is building their own selection.
   */
  canRemove(recipient) {
    const discussion = this.attrs.discussion;

    if (!discussion) return true;

    if (recipient instanceof User) return !!discussion.canEditUserRecipients();

    return !!discussion.canEditGroupRecipients();
  }
}
